import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  DocumentServingService,
  ItemDocumentReferenceStore,
  TermSheetStore,
} from '@qtek/libs/store';
import {
  TranslationCoreModule,
  TranslationCoreService,
} from '@qtek/libs/translation-core';
import {
  AccessLevel,
  DocumentAttribute,
  DocumentMeta,
  DocumentMetaDataType,
  DocumentReferenceEntityType,
  DocumentType,
  SchemaProperty,
  VirusScanStatus,
} from '@qtek/shared/models';
import { buildDocumentReferenceFormData } from '@qtek/shared/utils';
import { filter, map, race, switchMap, take, throwError, timer } from 'rxjs';
import {
  buildConflictReview,
  ConflictRow,
  FieldSelection,
  parseFieldValue,
} from './credit-agreement-extraction.utils';

export const benchmarkPublishDialogConfig: MatDialogConfig = {
  panelClass: ['qt-dialog', 'benchmark-publish-dialog'],
  width: '640px',
  maxWidth: '95vw',
  maxHeight: '90vh',
  disableClose: true,
  autoFocus: false,
};

/**
 * Which extractor each mode's upload should run — never the same type.
 * `benchmark-publish` gates on a signed credit agreement (SD-1909);
 * `upload-term-sheet` validates an actual term sheet (SD-2045). v1 shared one
 * dtp for both for lack of a CA extractor; v2 has `CreditAgreementExtractor`.
 */
const DIALOG_MODE_DOCUMENT_TYPE: Record<
  BenchmarkPublishDialogMode,
  DocumentType
> = {
  'benchmark-publish': DocumentType.CREDIT_AGREEMENT,
  'upload-term-sheet': DocumentType.TERM_SHEET,
};
const DIALOG_MODE_DATA_TYPE: Record<
  BenchmarkPublishDialogMode,
  DocumentMetaDataType
> = {
  'benchmark-publish': DocumentMetaDataType.CREDIT_AGREEMENT,
  'upload-term-sheet': DocumentMetaDataType.TERM_SHEET,
};
// Both possible extraction outcomes — connect watches for either so a wrong-type result
// (e.g. DocumentClassifier racing our PUT, dev-only) fails fast instead of silently
// dropping and burning the full EXTRACTION_TIMEOUT_MS.
const RECOGNIZED_DATA_TYPES = [
  DocumentMetaDataType.TERM_SHEET,
  DocumentMetaDataType.CREDIT_AGREEMENT,
];
const EXTRACTION_TIMEOUT_MS = 120000;
// Budget for the virus-scan wait alone — separate from EXTRACTION_TIMEOUT_MS,
// which only starts once extraction has actually been requested.
const SCAN_TIMEOUT_MS = 60000;

/**
 * The virus scan didn't settle within SCAN_TIMEOUT_MS. Not a failure: the scan is
 * usually just queued behind other documents (SD-2963).
 */
class ScanStillPendingError extends Error {
  constructor() {
    super('virus scan still pending');
  }
}

/** Which wait ran out while the document was still being processed (SD-2963). */
type StillProcessingStage = 'scan' | 'extraction';

export type BenchmarkPublishDialogMode =
  | 'benchmark-publish'
  | 'upload-term-sheet';

interface BenchmarkPublishDialogCopy {
  titleKey: string;
  helperKey: string;
  uploadCtaKey: string;
  retryUploadCtaKey: string;
  extractingStatusKey: string;
  extractionErrorTitleKey: string;
  extractionErrorBodyKey: string;
  noConflictsTitleKey: string;
  noConflictsBodyKey: string;
  reviewNoteKey?: string;
  conflictsActionKey: string;
  noConflictsActionKey: string;
  showNoConflictsActionIcon: boolean;
  scannedWarning: string;
  scannedContinueLabel: string;
  stillProcessingNotice: string;
}

const BENCHMARK_PUBLISH_DIALOG_COPY: Record<
  BenchmarkPublishDialogMode,
  BenchmarkPublishDialogCopy
> = {
  'benchmark-publish': {
    titleKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_TITLE',
    helperKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_HELPER',
    uploadCtaKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_UPLOAD_CTA',
    retryUploadCtaKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_RETRY_UPLOAD_CTA',
    extractingStatusKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_EXTRACTION_STATUS',
    extractionErrorTitleKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_ERROR_TITLE',
    extractionErrorBodyKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_ERROR_BODY',
    noConflictsTitleKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_SUCCESS_TITLE',
    noConflictsBodyKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_SUCCESS_BODY',
    conflictsActionKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_CONFLICTS_ACTION',
    noConflictsActionKey: 'GNR_BENCHMARK_PUBLISH_DIALOG_NO_CONFLICTS_ACTION',
    showNoConflictsActionIcon: false,
    scannedWarning:
      'The uploaded Credit Agreement appears to be scanned. We are unable to verify deal information against a document source of truth. You may still proceed with publishing.',
    scannedContinueLabel: 'Proceed with publishing',
    stillProcessingNotice:
      'Your Credit Agreement was uploaded and is still being processed. This can take a few minutes when many documents are being processed at once. Check again in a minute; there is no need to upload it again.',
  },
  'upload-term-sheet': {
    titleKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_TITLE',
    helperKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_HELPER',
    uploadCtaKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_UPLOAD_CTA',
    retryUploadCtaKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_RETRY_UPLOAD_CTA',
    extractingStatusKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_EXTRACTION_STATUS',
    extractionErrorTitleKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_ERROR_TITLE',
    extractionErrorBodyKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_ERROR_BODY',
    noConflictsTitleKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_SUCCESS_TITLE',
    noConflictsBodyKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_SUCCESS_BODY',
    reviewNoteKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_REVIEW_NOTE',
    conflictsActionKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_CONFLICTS_ACTION',
    noConflictsActionKey: 'GNR_UPLOAD_TERM_SHEET_DIALOG_NO_CONFLICTS_ACTION',
    showNoConflictsActionIcon: true,
    scannedWarning:
      'This document appears to be scanned. For best results, please upload a true digital PDF. You can also fill out the deal manually if a digital version is not available.',
    scannedContinueLabel: 'Fill out manually',
    stillProcessingNotice:
      'Your Term Sheet was uploaded and is still being processed. This can take a few minutes when many documents are being processed at once. Check again in a minute; there is no need to upload it again.',
  },
};

export interface BenchmarkPublishDialogData {
  itmId: string;
  dealName: string;
  /** Merged form snapshot used as the "current value" side of the conflict diff. */
  currentValues: Record<string, unknown> | null;
  properties?: Record<string, SchemaProperty> | null;
  mode?: BenchmarkPublishDialogMode;
}

export interface BenchmarkPublishDialogResult {
  /** Path-keyed values to patch into the deal form before save. */
  resolvedValues: Record<string, unknown>;
  /**
   * Reference (not document) id of the uploaded doc that should be retained
   * and classified by the caller — `ItemDocumentReferenceStore.updateDocument`
   * and `.remove` both key off this id, not the underlying document's.
   */
  uploadedReferenceId?: string | null;
  manualEntryRequested?: boolean;
}

type Step = 'upload' | 'review';
type UploadPhase = 'idle' | 'uploading' | 'extracting';
type ReviewState = 'has-conflicts' | 'no-conflicts' | 'extraction-failed';

@Component({
  selector: 'qt-benchmark-publish-dialog',
  templateUrl: './benchmark-publish-dialog.component.html',
  styleUrls: ['./benchmark-publish-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslationCoreModule,
  ],
})
export class BenchmarkPublishDialogComponent implements OnDestroy {
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput2') fileInput2?: ElementRef<HTMLInputElement>;

  private readonly dialogRef = inject(
    MatDialogRef<BenchmarkPublishDialogComponent, BenchmarkPublishDialogResult>
  );
  private readonly data = inject<BenchmarkPublishDialogData>(MAT_DIALOG_DATA);
  private readonly documentsStore = inject(ItemDocumentReferenceStore);
  private readonly documentServingService = inject(DocumentServingService);
  private readonly termSheetStore = inject(TermSheetStore);
  private readonly docMeta$ = toObservable(this.termSheetStore.docMeta);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translationService = inject(TranslationCoreService);

  readonly mode: BenchmarkPublishDialogMode =
    this.data.mode ?? 'benchmark-publish';
  readonly dialogCopy = BENCHMARK_PUBLISH_DIALOG_COPY[this.mode];
  private readonly extractionDocType = DIALOG_MODE_DOCUMENT_TYPE[this.mode];
  private readonly extractionDataType = DIALOG_MODE_DATA_TYPE[this.mode];
  readonly dealName =
    this.data.dealName ||
    this.translationService.instant('GNR_UPLOAD_REVIEW_UNTITLED_DEAL');

  readonly step = signal<Step>('upload');
  readonly uploadPhase = signal<UploadPhase>('idle');
  readonly reviewState = signal<ReviewState>('has-conflicts');
  readonly selectedFile = signal<File | null>(null);
  readonly conflictRows = signal<ConflictRow[]>([]);
  readonly saving = signal(false);
  readonly scanned = signal(false);
  /**
   * Set when a wait ran out while the uploaded document was still queued for its scan
   * or extraction. The document is kept and checked again on request instead of being
   * reported as unreadable (SD-2963).
   */
  readonly stillProcessing = signal<StillProcessingStage | null>(null);

  dragCounter = 0;

  /**
   * Tracked so cancel/replace can clean up the uploaded doc — and so confirm
   * can hand the *reference* id back to the caller (`documentId` is the
   * underlying document, only used to key the extraction subscription).
   */
  private uploadedReferenceId: string | null = null;
  private uploadedDocumentId: string | null = null;
  /** Per-upload token; flipping `cancelled` lets a late upload-success clean up its own orphan. */
  private activeUploadToken: { cancelled: boolean } | null = null;
  private mysid: string | null = null;

  readonly canAdvanceFromUpload = computed(
    () =>
      this.selectedFile() !== null &&
      this.uploadPhase() === 'idle' &&
      !this.scanned() &&
      this.stillProcessing() === null
  );

  readonly summaryText = computed(() => {
    const rows = this.conflictRows();
    const conflicts = rows.filter(r => !r.isCurrentEmpty).length;
    const empties = rows.filter(r => r.isCurrentEmpty).length;
    const parts: string[] = [];
    if (conflicts) {
      parts.push(
        this.translationService.instant(
          conflicts === 1
            ? 'GNR_UPLOAD_REVIEW_SUMMARY_CONFLICT'
            : 'GNR_UPLOAD_REVIEW_SUMMARY_CONFLICTS',
          [conflicts]
        )
      );
    }
    if (empties) {
      parts.push(
        this.translationService.instant(
          empties === 1
            ? 'GNR_UPLOAD_REVIEW_SUMMARY_EMPTY_FIELD'
            : 'GNR_UPLOAD_REVIEW_SUMMARY_EMPTY_FIELDS',
          [empties]
        )
      );
    }
    return parts.length
      ? this.translationService.instant(
          'GNR_UPLOAD_REVIEW_SUMMARY_FOUND_IN_DOCUMENT',
          [parts.join(' · ')]
        )
      : this.translationService.instant(
          'GNR_UPLOAD_REVIEW_SUMMARY_DIFFERENCES_FOUND'
        );
  });

  // ─── File pick / drag-drop ────────────────────────────

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (file) this.acceptFile(file);
    if (input) input.value = '';
  }

  handleDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter = 0;
    const file = event.dataTransfer?.files?.[0];
    if (file) this.acceptFile(file);
  }

  dragEnter(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter++;
  }

  dragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter = Math.max(0, this.dragCounter - 1);
  }

  dragOver(event: DragEvent): void {
    event.preventDefault();
  }

  private acceptFile(file: File): void {
    if (!this.isAcceptedExt(file)) return;
    // Mid-upload swap: cancel the prior listener so it disposes the late-arriving doc.
    const wasMidUpload =
      this.uploadPhase() !== 'idle' && !this.uploadedReferenceId;
    if (wasMidUpload && this.activeUploadToken) {
      this.activeUploadToken.cancelled = true;
    }
    const wasScanned = this.scanned() || this.stillProcessing() !== null;
    this.disposeUploadedDoc();
    this.selectedFile.set(file);
    // Auto-start on retry-from-failed or mid-upload swap; otherwise wait for Next.
    if (this.step() === 'review' || wasMidUpload || wasScanned) {
      this.step.set('upload');
      this.reviewState.set('has-conflicts');
      this.startUpload();
    }
  }

  private isAcceptedExt(file: File): boolean {
    const name = file.name.toLowerCase();
    return /\.pdf$/.test(name);
  }

  removeFile(event?: Event): void {
    event?.stopPropagation();
    if (this.uploadPhase() !== 'idle') return;
    this.disposeUploadedDoc();
    this.selectedFile.set(null);
  }

  // ─── Upload + extraction ──────────────────────────────

  next(): void {
    if (!this.canAdvanceFromUpload()) return;
    this.startUpload();
  }

  private startUpload(): void {
    const file = this.selectedFile();
    if (!file) return;

    this.scanned.set(false);
    this.uploadPhase.set('uploading');

    // Fresh token per upload — prior listeners keep their own.
    const token = { cancelled: false };
    this.activeUploadToken = token;

    // `documentType` is deliberately not sent here — `upload-tokenless` has no
    // such parameter and drops it; the classify PUT below is the only place
    // that actually sets it.
    const formData = buildDocumentReferenceFormData({
      file,
      fileName: file.name,
      entityType: DocumentReferenceEntityType.ITEM,
      entityId: this.data.itmId,
      attribute: DocumentAttribute.ITEM_ATTACHMENT,
      accessLevel: AccessLevel.SHARED,
      description: file.name,
    });

    // Closure-captured so the cleanup branch survives component destruction.
    const documentsStore = this.documentsStore;

    // No takeUntilDestroyed: a mid-upload Cancel must still wait for the HTTP to land so we can dispose the orphan.
    documentsStore.upload(formData).subscribe({
      next: reference => {
        const referenceId = reference?._id;
        const documentId = reference?.document?._id;
        if (token.cancelled) {
          if (referenceId) {
            documentsStore.remove(referenceId).subscribe();
          }
          return;
        }
        if (referenceId && documentId) {
          this.uploadedReferenceId = referenceId;
          this.uploadedDocumentId = documentId;
          this.requestExtraction(
            referenceId,
            documentId,
            reference.document?.virusScanStatus,
            token
          );
        } else {
          this.handleExtractionFailure();
        }
      },
      error: () => {
        if (!token.cancelled) {
          this.handleExtractionFailure();
        }
      },
    });
  }

  /**
   * ITEM_ATTACHMENT uploads skip extraction by default (SD-2668) — classifies
   * the doc as `extractionDocType` (nothing else will: an extraction-request
   * message skips `DocumentClassifier`, which is disabled outright in uat/prod
   * anyway), waits out the virus scan (extraction silently no-ops on an
   * unscanned doc), then opts the doc into extraction before subscribing. A scan
   * that is merely slow is reported as still processing, not as a failure.
   */
  private requestExtraction(
    referenceId: string,
    documentId: string,
    virusScanStatus: VirusScanStatus | undefined,
    token: { cancelled: boolean }
  ): void {
    const documentsStore = this.documentsStore;

    documentsStore
      .updateDocument(referenceId, {
        document: { documentType: this.extractionDocType },
      })
      .pipe(
        switchMap(() =>
          documentsStore.whenDocumentScanSettled(
            referenceId,
            this.data.itmId,
            virusScanStatus,
            SCAN_TIMEOUT_MS
          )
        ),
        switchMap(outcome => {
          if (outcome === 'ready') {
            return this.documentServingService.requestExtraction(
              `${DocumentReferenceEntityType.ITEM}=${referenceId}`
            );
          }
          return throwError(() =>
            outcome === 'timeout'
              ? new ScanStillPendingError()
              : new Error(`virus scan ${outcome}`)
          );
        })
      )
      .subscribe({
        next: response => {
          if (token.cancelled) {
            return;
          }
          const result = response?.res;
          if (result?.scanned && !result.extractionRequested) {
            this.uploadPhase.set('idle');
            this.scanned.set(true);
            return;
          }
          this.subscribeExtraction(documentId);
        },
        error: error => {
          if (token.cancelled) {
            return;
          }
          if (error instanceof ScanStillPendingError) {
            this.showStillProcessing('scan');
          } else {
            this.handleExtractionFailure();
          }
        },
      });
  }

  /**
   * Checks the same uploaded document again after a wait ran out: its scan, then the
   * extraction request, or the extraction result, which a fresh subscription reads
   * even if it arrived in between. Never uploads the file again.
   */
  checkAgain(): void {
    const stage = this.stillProcessing();
    const referenceId = this.uploadedReferenceId;
    const documentId = this.uploadedDocumentId;
    const token = this.activeUploadToken;
    if (!stage || !referenceId || !documentId || !token) return;

    this.stillProcessing.set(null);
    if (stage === 'scan') {
      this.uploadPhase.set('uploading');
      this.requestExtraction(referenceId, documentId, undefined, token);
    } else {
      this.subscribeExtraction(documentId);
    }
  }

  private showStillProcessing(stage: StillProcessingStage): void {
    this.unsubscribeExtraction();
    this.uploadPhase.set('idle');
    this.step.set('upload');
    this.stillProcessing.set(stage);
  }

  replaceScannedDocument(): void {
    this.fileInput?.nativeElement.click();
  }

  continuePastScannedWarning(): void {
    if (this.saving()) return;
    const uploadedReferenceId = this.uploadedReferenceId;
    if (!uploadedReferenceId) return;
    this.saving.set(true);

    if (this.mode === 'benchmark-publish') {
      this.documentServingService
        .requestExtraction(
          `${DocumentReferenceEntityType.ITEM}=${uploadedReferenceId}`,
          true
        )
        .subscribe({ error: () => undefined });
    }

    this.uploadedReferenceId = null;
    this.uploadedDocumentId = null;
    this.activeUploadToken = null;
    this.dialogRef.close({
      resolvedValues: {},
      uploadedReferenceId,
      manualEntryRequested: this.mode === 'upload-term-sheet',
    });
  }

  private subscribeExtraction(documentId: string): void {
    this.uploadPhase.set('extracting');
    this.mysid = `bench_publish_${documentId}_${Date.now()}`;
    const mysid = this.mysid;

    this.termSheetStore.connect({
      mysid,
      documentId,
      dataType: RECOGNIZED_DATA_TYPES,
    });

    const docMeta$ = this.docMeta$.pipe(
      map(docMeta => docMeta[mysid]),
      filter(Boolean),
      take(1)
    );

    race(docMeta$, timer(EXTRACTION_TIMEOUT_MS))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (typeof value === 'number') {
          // The timer won: the extraction is still queued, not failed (SD-2963).
          this.showStillProcessing('extraction');
          return;
        }
        if (!value || typeof value !== 'object') {
          this.handleExtractionFailure();
          return;
        }
        if (value.dataType !== this.extractionDataType) {
          // Right document, wrong type — DocumentClassifier raced our classify PUT
          // (dev-only; disabled in uat/prod). Definitive, not a timeout: fail now.
          console.warn(
            `[benchmark-publish] expected ${this.extractionDataType}, got ${value.dataType}`
          );
          this.handleExtractionFailure();
          return;
        }
        this.handleExtractionResult(value as DocumentMeta);
      });
  }

  private handleExtractionResult(response: DocumentMeta): void {
    this.uploadPhase.set('idle');
    const data = response?.containers?.[0]?.data as
      | Record<string, string | null>
      | undefined;

    if (!data || data['isValid'] !== 'true') {
      this.handleExtractionFailure();
      return;
    }

    const review = buildConflictReview({
      extracted: data,
      currentValues: this.data.currentValues,
      properties: this.data.properties ?? undefined,
    });

    this.step.set('review');
    if (review.rows.length === 0) {
      this.reviewState.set('no-conflicts');
      this.conflictRows.set([]);
    } else {
      this.reviewState.set('has-conflicts');
      this.conflictRows.set(review.rows);
    }
  }

  private handleExtractionFailure(): void {
    this.uploadPhase.set('idle');
    this.step.set('review');
    this.reviewState.set('extraction-failed');
    this.conflictRows.set([]);
    this.unsubscribeExtraction();
  }

  private unsubscribeExtraction(): void {
    if (this.mysid) {
      this.termSheetStore.disconnect(this.mysid);
      this.mysid = null;
    }
  }

  // ─── Review interactions ──────────────────────────────

  setSelection(key: string, selection: FieldSelection): void {
    this.conflictRows.update(rows =>
      rows.map(r => (r.key === key ? { ...r, selection } : r))
    );
  }

  acceptAllFromDocument(): void {
    this.conflictRows.update(rows =>
      rows.map(r => ({ ...r, selection: 'document' as FieldSelection }))
    );
  }

  back(): void {
    if (this.saving()) return;
    // Dispose the prior doc so a re-upload (Next on same file) doesn't orphan it.
    this.disposeUploadedDoc();
    this.unsubscribeExtraction();
    this.step.set('upload');
    this.uploadPhase.set('idle');
  }

  // ─── Confirm + cancel ─────────────────────────────────

  confirmSelection(): void {
    if (this.saving()) return;
    this.saving.set(true);

    const resolved: Record<string, unknown> = {};
    for (const row of this.conflictRows()) {
      if (row.selection === 'document') {
        resolved[row.key] = parseFieldValue(row.documentRaw, row.type);
      }
    }

    const uploadedReferenceId = this.uploadedReferenceId;

    // Clear ids/token so destroy-time cleanup doesn't delete the confirmed doc.
    this.uploadedReferenceId = null;
    this.uploadedDocumentId = null;
    this.activeUploadToken = null;
    this.unsubscribeExtraction();
    this.dialogRef.close({ resolvedValues: resolved, uploadedReferenceId });
  }

  cancel(): void {
    if (this.saving()) return;
    if (this.activeUploadToken) {
      this.activeUploadToken.cancelled = true;
      this.activeUploadToken = null;
    }
    this.disposeUploadedDoc();
    this.unsubscribeExtraction();
    this.dialogRef.close();
  }

  // ─── Cleanup ──────────────────────────────────────────

  /** Deletes a doc that was uploaded but won't be published (cancel / replace / extraction-fail). */
  private disposeUploadedDoc(): void {
    this.scanned.set(false);
    this.stillProcessing.set(null);
    if (!this.uploadedReferenceId) return;
    this.documentsStore.remove(this.uploadedReferenceId).subscribe();
    this.uploadedReferenceId = null;
    this.uploadedDocumentId = null;
  }

  ngOnDestroy(): void {
    // Host destruction without confirm — clean up any uploaded or in-flight doc.
    if (this.activeUploadToken) {
      this.activeUploadToken.cancelled = true;
      this.activeUploadToken = null;
    }
    this.disposeUploadedDoc();
    this.unsubscribeExtraction();
  }
}
